import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase/firebaseConfig';
import {
  DEFAULT_SHOWROOM_MESSAGE,
  DEFAULT_SHOWROOM_TITLE,
  SHOWROOM_COLLECTION,
  SHOWROOM_SETTINGS_ID,
} from '../components/showroom/showroomSettings';

const settingsRef = () => doc(firestore, SHOWROOM_COLLECTION, SHOWROOM_SETTINGS_ID);

function ShowroomTopTextEditor() {
  const [title, setTitle] = useState(DEFAULT_SHOWROOM_TITLE);
  const [message, setMessage] = useState(DEFAULT_SHOWROOM_MESSAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const isFormValid = title.trim() !== '' && message.trim() !== '';

  useEffect(() => {
    const fetchCurrentText = async () => {
      try {
        const snap = await getDoc(settingsRef());
        if (snap.exists()) {
          const data = snap.data();
          if (typeof data.title === 'string' && data.title.trim() !== '') {
            setTitle(data.title);
          }
          if (typeof data.message === 'string' && data.message.trim() !== '') {
            setMessage(data.message);
          }
        }
      } catch (error) {
        console.error('ショールームテキストの取得に失敗:', error);
        setFetchError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentText();
  }, []);

  const saveText = async () => {
    if (!isFormValid || isSaving) return;

    try {
      setIsSaving(true);
      await setDoc(
        settingsRef(),
        {
          title: title.trim(),
          message: message.replace(/\r\n/g, '\n').trim(),
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setShowNotification(true);
    } catch (error) {
      console.error('ショールームテキストの保存に失敗:', error);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      width: '100%',
    },
    form: {
      width: '100%',
      maxWidth: '800px',
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
      textAlign: 'left',
      position: 'relative',
    },
    sectionTitle: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    inputGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    inputField: {
      width: '100%',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: '280px',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
      lineHeight: '1.8',
      fontFamily: 'inherit',
      fontSize: '14px',
      resize: 'vertical',
    },
    hint: {
      marginTop: '8px',
      color: '#666',
      fontSize: '12px',
    },
    submitButton: {
      padding: '10px 20px',
      backgroundColor: isFormValid && !isSaving ? '#110b41ff' : '#ccc',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: isFormValid && !isSaving ? 'pointer' : 'not-allowed',
      width: '100%',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: isSaving ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '8px',
      zIndex: 10,
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    notification: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
      marginTop: '20px',
    },
    okButton: {
      padding: '10px',
      backgroundColor: '#110b41ff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginTop: '20px',
    },
    error: {
      color: 'red',
      marginBottom: '20px',
    },
    emptyText: {
      color: '#666',
      fontSize: '14px',
      marginBottom: '20px',
    },
  };

  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div style={styles.loadingOverlay}>
          <span style={styles.loadingText}>保存中...</span>
        </div>

        <div style={styles.sectionTitle}>
          <p>トップタイトル・メッセージ</p>
        </div>

        {isLoading && <p style={styles.emptyText}>読み込み中...</p>}
        {fetchError && <p style={styles.error}>エラー: {fetchError}</p>}

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="showroomTitle">タイトル:</label>
          <input
            id="showroomTitle"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={styles.inputField}
            disabled={isSaving || isLoading}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="showroomMessage">メッセージ:</label>
          <textarea
            id="showroomMessage"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={styles.textarea}
            disabled={isSaving || isLoading}
          />
          <p style={styles.hint}>改行はそのままフロントに反映されます。</p>
        </div>

        <button
          type="button"
          onClick={saveText}
          style={styles.submitButton}
          disabled={!isFormValid || isSaving || isLoading}
        >
          {isSaving ? '保存中...' : isFormValid ? 'タイトルとメッセージを登録' : 'タイトルとメッセージを入力してください'}
        </button>
      </form>

      {showNotification && (
        <div style={styles.notification}>
          <p>タイトルとメッセージを登録しました。</p>
          <button onClick={() => setShowNotification(false)} style={styles.okButton}>OK</button>
        </div>
      )}
    </div>
  );
}

export default ShowroomTopTextEditor;
